import {useMemo} from 'react';

import {apiClient} from 'common/util/api-client';
import type {
  CohortSyncTime,
  CohortSyncTimeRequest,
  FeatureFlagApiPayload,
  FeatureFlagApiResponse,
  FeatureFlagHistoryData,
  FeatureFlagLimitsResults,
  FeatureFlagUsersRequest,
  FeatureFlagUsersResults,
} from 'app_api/projects/feature_flags/types';

interface UseFeatureFlagsApiProps {
  projectId: string | number;
  workspaceId?: string | number;
}

type ListFeatureFlagsResponse = FeatureFlagApiResponse[];
type FeatureFlagHistoryResponse = FeatureFlagHistoryData;
type GetFeatureFlagRequest = {id: string};
type GetFeatureFlagHistoryRequest = {id: string; page?: string};
type CreateFeatureFlagRequest = FeatureFlagApiPayload;
type UpdateFeatureFlagRequest = FeatureFlagApiPayload & {id: string};
type DeleteFeatureFlagRequest = {id: string};
type DuplicateFeatureFlagRequest = {id: string};
type GetFeatureFlagUsersRequest = FeatureFlagUsersRequest;
type GetCohortSyncTimeRequest = CohortSyncTimeRequest;

const DEFAULT_EXPERIMENT_VARIANT_NAME = `test`;
const DEFAULT_CONTROL_VARIANT_NAME = `control`;
const DEFAULT_ON_VARIANT_NAME = `On`;

const DEFAULT_OFF_VARIANT_NAME = `Off`;
export const DEFAULT_DYNAMIC_CONFIG_VARIANT_NAME = `$dynamic_config`;
export const DEFAULT_DYNAMIC_CONFIG_OFF_NAME = `$dynamic_config_disabled`;

export const DEVICE_ID = `device_id`;
export const DISTINCT_ID = `distinct_id`;

export const DEFAULT_EXPERIMENT_VARIANTS = [
  {
    key: DEFAULT_CONTROL_VARIANT_NAME,
    value: DEFAULT_CONTROL_VARIANT_NAME,
    description: ``,
    split: 0.5,
    is_control: true,
    is_sticky: false,
  },
  {
    key: DEFAULT_EXPERIMENT_VARIANT_NAME,
    value: DEFAULT_EXPERIMENT_VARIANT_NAME,
    description: ``,
    split: 0.5,
    is_control: false,
    is_sticky: false,
  },
];

export const DEFAULT_FEATURE_GATE_VARIANTS = [
  {
    key: DEFAULT_ON_VARIANT_NAME,
    value: true,
    description: ``,
    split: 1,
    is_control: false,
    is_sticky: false,
  },
  {
    key: DEFAULT_OFF_VARIANT_NAME,
    value: false,
    description: ``,
    split: 0,
    is_control: true,
    is_sticky: false,
  },
];

export const DEFAULT_DYNAMIC_CONFIG_VARIANTS = [
  {
    key: DEFAULT_DYNAMIC_CONFIG_VARIANT_NAME,
    value: `{}`,
    description: ``,
    split: 1,
    is_control: false,
    is_sticky: false,
  },
  {
    key: DEFAULT_DYNAMIC_CONFIG_OFF_NAME,
    value: `{}`,
    description: ``,
    split: 0,
    is_control: true,
    is_sticky: false,
  },
];

export const buildNewFeatureFlag = (isDynamicConfig?: boolean): FeatureFlagApiPayload => {
  return {
    name: `New Feature Flag`,
    key: `new_feature_flag_${Date.now()}`,
    context: `distinct_id`,
    serving_method: `client`,
    ruleset: {
      variants: isDynamicConfig ? DEFAULT_DYNAMIC_CONFIG_VARIANTS : DEFAULT_FEATURE_GATE_VARIANTS,
      rollout: [],
    },
    tags: [],
  };
};

export function featureFlagsApi(props: UseFeatureFlagsApiProps) {
  const basePath = props.workspaceId
    ? `/api/app/projects/${props.projectId}/workspaces/${props.workspaceId}/feature-flags`
    : `/api/app/projects/${props.projectId}/feature-flags`;
  return {
    listFeatureFlags: () => apiClient.get<ListFeatureFlagsResponse>(basePath),

    getFeatureFlag: ({id}: GetFeatureFlagRequest) => {
      return apiClient.get<FeatureFlagApiResponse>(`${basePath}/${id}`);
    },

    getFeatureFlagHistory: ({id, page}: GetFeatureFlagHistoryRequest) => {
      const query = page ? `?page=` + page : ``;
      return apiClient.get<FeatureFlagHistoryResponse>(`${basePath}/${id}/history` + query);
    },

    createFeatureFlag: (flag: CreateFeatureFlagRequest) => {
      return apiClient.post<FeatureFlagApiResponse>(basePath, flag);
    },

    updateFeatureFlag: ({id, ...flag}: UpdateFeatureFlagRequest) => {
      return apiClient.put<FeatureFlagApiResponse>(`${basePath}/${id}`, flag);
    },

    deleteFeatureFlag: ({id}: DeleteFeatureFlagRequest) => {
      return apiClient.delete(`${basePath}/${id}`);
    },

    duplicateFeatureFlag: ({id}: DuplicateFeatureFlagRequest) => {
      return apiClient.post<FeatureFlagApiResponse>(`${basePath}/${id}/duplicate`, {});
    },

    getFeatureFlagLimits: () => {
      return apiClient.get<FeatureFlagLimitsResults>(`${basePath}/limits`);
    },

    getFeatureFlagUsers: (request: GetFeatureFlagUsersRequest, signal?: AbortSignal) => {
      return apiClient.post<FeatureFlagUsersResults>(`${basePath}/users`, request, {abortSignal: signal});
    },

    getCohortSyncTime: (request: GetCohortSyncTimeRequest) => {
      return apiClient.post<CohortSyncTime>(`${basePath}/last-cohort-sync-time`, request);
    },
  } as const;
}

export function useFeatureFlagsApi({projectId, workspaceId}: UseFeatureFlagsApiProps) {
  const api = useMemo(() => featureFlagsApi({projectId, workspaceId}), [projectId, workspaceId]);
  return api;
}
